import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import OperationalPanel from '../../component/executive/operationalPanel/OperationalPanel';
import CoreModules from '../../component/executive/coreModules/CoreModules';
import MarketConflictSummary from '../../component/PriceConflict/MarketConflictSummary';
import RegionalMapSection from '../../component/executive/Map/RegionalMap';
import FieldDashboard from './FieldDashboard';
import StateDashboard from './StateDashboard';
import { getDashboardBlueprintForRole } from '../../config/rbacConfig';

const ExecutiveOverview = () => {
  const { user } = useAuth();
  const { unreadCount, notifLoading } = useNotifications();
  const hasToasted = useRef(false);

  // Show a welcome or notification-alert toast once per dashboard visit,
  // after the notifications API has resolved. Fires for all roles because
  // hooks run before the role-based early returns below.
  useEffect(() => {
    if (notifLoading || hasToasted.current) return;
    hasToasted.current = true;
    if (unreadCount > 0) {
      toast.info(
        `You have ${unreadCount} new notification${unreadCount > 1 ? 's' : ''}. ` +
        'Please check your notification bar.',
        { autoClose: 6000 },
      );
    } else {
      toast.success(
        `Welcome back, ${user?.name ?? 'User'}! Your dashboard is up to date.`,
        { autoClose: 4000 },
      );
    }
  }, [notifLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const blueprint = getDashboardBlueprintForRole(user?.roleId);
  const isNationalRole = ['R01', 'R02', 'R03', 'R06'].includes(user?.roleId);
  const visibilityLabel =
    user?.roleId === 'R03'
      ? 'Assigned modules only'
      : isNationalRole
        ? 'National visibility'
        : 'Role visibility';
  const coreModulesTitle = user?.roleId === 'R03' ? 'Assigned Modules' : 'Core Modules';

  if (user?.roleId === 'R05') return <FieldDashboard />;
  if (user?.roleId === 'R04') return <StateDashboard />;

  return (
    <div className="p-6 bg-white rounded-md shadow-md min-h-screen space-y-6">
      <section className="rounded-2xl border border-green-100 bg-gradient-to-br from-green-50 via-white to-emerald-50 px-6 py-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-3xl">
            <p className="text-[10px] font-bold uppercase tracking-widest text-green-600 mb-1">
              {blueprint.kicker}
            </p>
            <h1 className="text-2xl font-black text-gray-900">{blueprint.title}</h1>
            <p className="mt-2 text-sm text-gray-600">{blueprint.summary}</p>
          </div>
          <div className="rounded-full border border-green-200 bg-white px-3 py-1 text-xs font-semibold text-green-700">
            {visibilityLabel}
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {(blueprint.highlights ?? []).map((item) => (
            <div key={item.label} className="rounded-xl border border-green-100 bg-white/80 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {item.label}
              </p>
              <p className="mt-1 text-sm font-bold text-gray-900">{item.value}</p>
            </div>
          ))}
        </div>
      </section>

      <OperationalPanel />
      <CoreModules title={coreModulesTitle} />
      <MarketConflictSummary />
      <RegionalMapSection />
    </div>
  );
};

export default ExecutiveOverview;
